
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  UsersRound,
  LayoutList,
  Building2,
  Key,
  Palette,
  Shield,
  Database,
  UserCircle,
  Bell,
} from "lucide-react";
import type { ComponentType } from "react";
import Link from "@/components/ui/link-compat";
import { useAuth } from "@/contexts/AuthContext";
import { profileApi } from "@/lib/api/auth";

export interface NavItem {
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  trailing?: string;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
  adminOnly?: boolean;
}

export const navGroups: NavGroup[] = [
  {
    title: "Account",
    items: [
      { label: "Profile", href: "/dashboard/settings/profile", icon: UserCircle },
      { label: "Notifications", href: "/dashboard/settings/notifications", icon: Bell },
      { label: "Appearance", href: "/dashboard/settings/appearance", icon: Palette },
    ],
  },
  {
    title: "Workspace",
    items: [
      { label: "Team", href: "/dashboard/settings/team", icon: UsersRound },
      { label: "Deal Types", href: "/dashboard/settings/deal-types", icon: LayoutList },
      { label: "Organizations", href: "/dashboard/settings/organizations", icon: Building2 },
      { label: "API Keys", href: "/dashboard/settings/api-keys", icon: Key },
    ],
  },
  {
    title: "Developer",
    items: [
      {
        label: "Developer Tools",
        href: "/dashboard/settings/developer",
        icon: Database,
        trailing: "Sync status",
      },
    ],
  },
  {
    title: "Admin",
    adminOnly: true,
    items: [{ label: "Admin Panel", href: "/dashboard/settings/admin", icon: Shield }],
  },
];

interface MobileSettingsIndexProps {
  groups: NavGroup[];
  isSuperAdmin: boolean;
}

export function MobileSettingsIndex({ groups, isSuperAdmin }: MobileSettingsIndexProps) {
  const { currentOrg, logout } = useAuth();
  const profileQuery = useQuery({
    queryKey: ["profile"],
    queryFn: profileApi.get,
    staleTime: 5 * 60_000,
  });

  const visibleGroups = groups.filter((g) => !g.adminOnly || isSuperAdmin);

  const profile = profileQuery.data;
  const displayName = profile?.displayName?.trim() || currentOrg?.name || "Account";
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
  const subline = [profile?.email ?? "", currentOrg?.role].filter(Boolean).join(" · ");

  return (
    <div className="flex h-full flex-col gap-4">
      <Link
        href="/dashboard/settings/profile"
        className="flex items-center gap-2.5 rounded-[10px] border p-3.5"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
          {initials}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{displayName}</span>
          <span className="block truncate text-xs text-muted-foreground">{subline}</span>
        </span>
        <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>

      {visibleGroups.map((group) => (
        <div key={group.title}>
          <div className="px-1 pb-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {group.title}
          </div>
          {group.items.map((item, index) => {
            const first = index === 0;
            const last = index === group.items.length - 1;
            return (
              <Link
                key={item.href}
                href={item.href}
                className={`flex h-12 items-center gap-2.5 border px-3.5 text-sm ${
                  first ? "rounded-t-[10px]" : ""
                } ${last ? "rounded-b-[10px]" : "border-b-0"}`}
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.label}</span>
                {item.trailing && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                    {item.trailing}
                  </span>
                )}
                <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
              </Link>
            );
          })}
        </div>
      ))}

      <button
        onClick={logout}
        className="mt-auto flex h-11 shrink-0 items-center justify-center text-sm font-medium text-destructive"
      >
        Sign out
      </button>
    </div>
  );
}
